import { Box, Button, Drawer, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { RevOpsBrief } from "Models/RevOpsBrief";
import { advisor } from "Components/AdvisorTheme";
import { BriefFeedbackBundle, BriefSections, FeedbackAlert, formatCreatedAt, isStaleBrief, meaningfulSummary } from "Components/TodaysBrief";
import { formatComparison, humanizeMetric, uniqueSignals } from "./RevenueSnapshot";
import { BRIEF_SCOPE_DETAIL, ScopeChip } from "./TopPriorities";

interface FullBriefPanelProps {
  open: boolean;
  onClose: () => void;
  brief: RevOpsBrief | null | undefined;
  fb: BriefFeedbackBundle;
}

/** Complete brief: every section, item feedback and all metric signals. */
export default function FullBriefPanel({ open, onClose, brief, fb }: FullBriefPanelProps) {
  const summary = brief ? meaningfulSummary(brief.summary) : undefined;
  const signals = brief ? uniqueSignals(brief) : [];
  const byPipeline = signals.some(s => s.pipeline != null);
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 560 }, bgcolor: advisor.page }, role: "dialog", "aria-labelledby": "full-brief-title" } as any}
    >
      <Box sx={{ position: "sticky", top: 0, zIndex: 1, bgcolor: "#FFFFFF", borderBottom: `1px solid ${advisor.border}`, px: { xs: 2, sm: 3 }, py: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id="full-brief-title" component="h2" sx={{ fontSize: 18, fontWeight: 700, color: advisor.text }}>
            Today's RevOps Brief
          </Typography>
          {brief && (
            <Typography sx={{ fontSize: 13, color: advisor.muted }}>
              {brief.date} · Created {formatCreatedAt(brief.created_at)} · {isStaleBrief(brief) ? "Not today’s brief — daily data may be stale" : "Today"}
            </Typography>
          )}
        </Box>
        <Button onClick={onClose}>Close brief</Button>
      </Box>
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, display: "grid", gap: 2 }}>
        {!brief ? (
          <Typography>No revenue brief available yet.</Typography>
        ) : (
          <>
            <Box>
              <ScopeChip />
              <Typography sx={{ fontSize: 13, color: advisor.muted, mt: 0.75 }}>{BRIEF_SCOPE_DETAIL}</Typography>
            </Box>
            <FeedbackAlert fb={fb} />
            {summary && <Typography sx={{ fontSize: 15, color: advisor.text }}>{summary}</Typography>}
            <Box sx={{ bgcolor: "#FFFFFF", border: `1px solid ${advisor.border}`, borderRadius: advisor.radius, p: { xs: 2, sm: 2.5 } }}>
              <BriefSections brief={brief} fb={fb} />
            </Box>
            {signals.length > 0 && (
              <Box sx={{ bgcolor: "#FFFFFF", border: `1px solid ${advisor.border}`, borderRadius: advisor.radius, p: { xs: 2, sm: 2.5 }, overflowX: "auto" }}>
                <Typography component="h3" sx={{ fontSize: 13, fontWeight: 700, color: "#4B4763", letterSpacing: "0.08em", textTransform: "uppercase", mb: 1 }}>
                  Change signals
                </Typography>
                <Table size="small" aria-label="Brief change signals">
                  <TableHead><TableRow><TableCell>Metric</TableCell>{byPipeline && <TableCell>Pipeline</TableCell>}<TableCell align="right">Value</TableCell><TableCell align="right">DoD</TableCell><TableCell align="right">WoW</TableCell></TableRow></TableHead>
                  <TableBody>
                    {signals.map(s => (
                      <TableRow key={`${s.pipeline ?? ""}|${s.metric}`}>
                        <TableCell sx={{ overflowWrap: "anywhere" }}>{humanizeMetric(s.metric)}</TableCell>
                        {byPipeline && <TableCell sx={{ overflowWrap: "anywhere" }}>{s.pipeline_name ?? s.pipeline ?? "Not recorded"}</TableCell>}
                        <TableCell align="right">{s.current_value == null ? "Unavailable" : s.current_value.toLocaleString()}</TableCell>
                        <TableCell align="right">{formatComparison(s.dod_pct)}</TableCell>
                        <TableCell align="right">{formatComparison(s.wow_pct)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
}
